import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Spin,
  Button,
  Select,
  Input,
  InputNumber,
  message,
  Switch,
  Tooltip,
} from "antd";
import {
  getTemplateVisualizationData,
  getTemplateVisualizationImage,
  getTemplateTextPositions,
  getDataFilePreview,
  getFontFile,
} from "../../core/api/pdfParser";
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  LeftOutlined,
  RightOutlined,
  AlignLeftOutlined,
} from "@ant-design/icons";

const { TextArea } = Input;

/**
 * Replace placeholders in text with actual values from data
 */
function replacePlaceholders(text, data) {
  if (!text || !data) return text;

  return text.replace(/\{([A-Z0-9_]+)\}/gi, (match, key) => {
    const keyUpper = key.toUpperCase();
    const keyLower = key.toLowerCase();

    // Try multiple formats to find a match
    const keysToTry = [
      key, // exact: FIRSTNAME
      keyUpper, // upper: FIRSTNAME
      keyLower, // lower: firstname
      `{${key}}`, // with braces: {FIRSTNAME}
      `{${keyUpper}}`, // with braces upper: {FIRSTNAME}
      key.replace(/_/g, " "), // spaces: FIRST NAME
      key.replace(/_/g, ""), // no separator: FIRSTNAME
    ];

    for (const tryKey of keysToTry) {
      if (
        data[tryKey] !== undefined &&
        data[tryKey] !== null &&
        data[tryKey] !== ""
      ) {
        return String(data[tryKey]);
      }
    }

    // Try case-insensitive match on all data keys
    for (const dataKey of Object.keys(data)) {
      const dataKeyClean = dataKey.replace(/[{}]/g, "").toUpperCase();
      if (dataKeyClean === keyUpper) {
        const val = data[dataKey];
        if (val !== undefined && val !== null && val !== "") {
          return String(val);
        }
      }
    }

    // Return original placeholder if no match found
    return match;
  });
}

/**
 * Calculate snap lines when dragging near text guides
 * Only considers NEARBY text (within proximity threshold)
 * Returns clean, minimal snap lines
 */
function calculateSnapLines(region, textGuides, pageNum, snapThreshold = 4) {
  const snapLines = [];
  let snappedX = null;
  let snappedY = null;

  const regionLeft = region.x;
  const regionRight = region.x + region.width;
  const regionTop = region.y;
  const regionBottom = region.y + region.height;

  // Only consider text guides that are NEARBY (within 100 PDF points)
  const proximityThreshold = 100;
  const nearbyGuides = textGuides.filter((t) => {
    if (t.page !== pageNum) return false;
    const guideLeft = t.bbox.x0;
    const guideRight = t.bbox.x1;
    const guideTop = t.bbox.y0;
    const guideBottom = t.bbox.y1;

    // Check if guide is within proximity (horizontal OR vertical)
    const horizontallyClose =
      regionRight >= guideLeft - proximityThreshold &&
      regionLeft <= guideRight + proximityThreshold;
    const verticallyClose =
      regionBottom >= guideTop - proximityThreshold &&
      regionTop <= guideBottom + proximityThreshold;

    return horizontallyClose || verticallyClose;
  });

  // Check alignment with nearby guides only
  for (const guide of nearbyGuides) {
    const guideLeft = guide.bbox.x0;
    const guideRight = guide.bbox.x1;
    const guideTop = guide.bbox.y0;
    const guideBottom = guide.bbox.y1;

    // Only check horizontal alignment if we're horizontally close to this text
    const horizontallyAdjacent =
      Math.abs(regionRight - guideLeft) < 50 ||
      Math.abs(regionLeft - guideRight) < 50 ||
      (regionLeft < guideRight && regionRight > guideLeft);

    if (horizontallyAdjacent) {
      // Top alignment
      if (Math.abs(regionTop - guideTop) < snapThreshold) {
        const minX = Math.min(regionLeft, guideLeft);
        const maxX = Math.max(regionRight, guideRight);
        snapLines.push({
          type: "horizontal",
          y: guideTop,
          x1: minX,
          x2: maxX,
          color: "#1677ff",
        });
        if (snappedY === null) snappedY = guideTop;
      }
      // Bottom/baseline alignment - MOST IMPORTANT
      if (Math.abs(regionBottom - guideBottom) < snapThreshold) {
        const minX = Math.min(regionLeft, guideLeft);
        const maxX = Math.max(regionRight, guideRight);
        snapLines.push({
          type: "horizontal",
          y: guideBottom,
          x1: minX,
          x2: maxX,
          color: "#faad14",
        });
        if (snappedY === null) snappedY = guideBottom - region.height;
      }
    }

    // Only check vertical alignment if we're vertically close to this text
    const verticallyAdjacent =
      Math.abs(regionBottom - guideTop) < 50 ||
      Math.abs(regionTop - guideBottom) < 50 ||
      (regionTop < guideBottom && regionBottom > guideTop);

    if (verticallyAdjacent) {
      // Left edge to right edge of text (common: placing placeholder after text)
      if (Math.abs(regionLeft - guideRight) < snapThreshold) {
        const minY = Math.min(regionTop, guideTop);
        const maxY = Math.max(regionBottom, guideBottom);
        snapLines.push({
          type: "vertical",
          x: guideRight,
          y1: minY,
          y2: maxY,
          color: "#52c41a",
        });
        if (snappedX === null) snappedX = guideRight;
      }
      // Left edge alignment
      if (Math.abs(regionLeft - guideLeft) < snapThreshold) {
        const minY = Math.min(regionTop, guideTop);
        const maxY = Math.max(regionBottom, guideBottom);
        snapLines.push({
          type: "vertical",
          x: guideLeft,
          y1: minY,
          y2: maxY,
          color: "#ff4d4f",
        });
        if (snappedX === null) snappedX = guideLeft;
      }
    }
  }

  return { snapLines, snappedX, snappedY };
}

/**
 * Region Component - Renders a single bounding box on a page
 */
function Region({
  region,
  scale,
  isSelected,
  onSelect,
  onUpdate,
  drawMode,
  livePreview = false,
  previewData = null,
  fontMap = {},
  availableFonts = [],
  loadedWebFonts = {},
  textGuides = [],
  pageNum = 0,
  onSnapLinesChange,
  enableSnapping = true,
  isEditing = false,
  onStartEdit,
}) {
  const regionRef = useRef(null);
  const editRef = useRef(null);
  const [editValue, setEditValue] = useState("");
  const lastClickRef = useRef(0);

  useEffect(() => {
    if (isEditing) {
      setEditValue(region.content || "");
      setTimeout(() => editRef.current?.focus(), 0);
    }
  }, [isEditing]);

  const screenBox = {
    left: region.x * scale,
    top: region.y * scale,
    width: region.width * scale,
    height: region.height * scale,
  };

  const handleRegionClick = (e) => {
    e.stopPropagation();
    const now = Date.now();
    if (isSelected && now - lastClickRef.current < 400) {
      // Second click on already-selected region → enter edit mode
      lastClickRef.current = 0;
      if (!drawMode && onStartEdit) onStartEdit(region.id);
    } else {
      onSelect(region.id);
      lastClickRef.current = now;
    }
  };

  const finishEdit = () => {
    onUpdate(region.id, { content: editValue });
    if (onStartEdit) onStartEdit(null);
  };

  const handleEditKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === "Escape") {
      e.preventDefault();
      if (onStartEdit) onStartEdit(null);
    }
  };

  const handleMouseDown = (e, action = "move") => {
    if (drawMode || isEditing) return;
    e.stopPropagation();
    e.preventDefault();

    const startX = e.clientX;
    const startY = e.clientY;
    const startRegion = { ...region };
    let isDragging = false;
    const DRAG_THRESHOLD = 4;

    onSelect(region.id);

    const handleMouseMove = (moveE) => {
      const rawDx = moveE.clientX - startX;
      const rawDy = moveE.clientY - startY;

      if (!isDragging) {
        if (
          Math.abs(rawDx) < DRAG_THRESHOLD &&
          Math.abs(rawDy) < DRAG_THRESHOLD
        )
          return;
        isDragging = true;
      }

      const dx = rawDx / scale;
      const dy = rawDy / scale;

      let updates = {};

      if (action === "move") {
        let newX = Math.max(0, startRegion.x + dx);
        let newY = Math.max(0, startRegion.y + dy);

        if (textGuides.length > 0 && enableSnapping) {
          const tempRegion = { ...region, x: newX, y: newY };
          const { snapLines, snappedX, snappedY } = calculateSnapLines(
            tempRegion,
            textGuides,
            pageNum,
          );

          if (snappedX !== null) newX = snappedX;
          if (snappedY !== null) newY = snappedY;

          if (onSnapLinesChange) {
            onSnapLinesChange(snapLines);
          }
        }

        updates = { x: newX, y: newY };
      } else {
        let newWidth, newHeight, newX, newY;

        switch (action) {
          case "se":
            newWidth = Math.max(30, startRegion.width + dx);
            newHeight = Math.max(15, startRegion.height + dy);
            updates = { width: newWidth, height: newHeight };
            break;
          case "sw":
            newX = Math.min(
              startRegion.x + startRegion.width - 30,
              startRegion.x + dx,
            );
            newWidth = Math.max(30, startRegion.width - dx);
            newHeight = Math.max(15, startRegion.height + dy);
            updates = { x: newX, width: newWidth, height: newHeight };
            break;
          case "ne":
            newY = Math.min(
              startRegion.y + startRegion.height - 15,
              startRegion.y + dy,
            );
            newWidth = Math.max(30, startRegion.width + dx);
            newHeight = Math.max(15, startRegion.height - dy);
            updates = { y: newY, width: newWidth, height: newHeight };
            break;
          case "nw":
            newX = Math.min(
              startRegion.x + startRegion.width - 30,
              startRegion.x + dx,
            );
            newY = Math.min(
              startRegion.y + startRegion.height - 15,
              startRegion.y + dy,
            );
            newWidth = Math.max(30, startRegion.width - dx);
            newHeight = Math.max(15, startRegion.height - dy);
            updates = { x: newX, y: newY, width: newWidth, height: newHeight };
            break;
        }

        if (textGuides.length > 0 && enableSnapping) {
          const tempRegion = {
            ...region,
            x: updates.x ?? region.x,
            y: updates.y ?? region.y,
            width: updates.width ?? region.width,
            height: updates.height ?? region.height,
          };
          const { snapLines } = calculateSnapLines(
            tempRegion,
            textGuides,
            pageNum,
          );
          if (onSnapLinesChange) {
            onSnapLinesChange(snapLines);
          }
        }
      }

      onUpdate(region.id, updates);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      if (onSnapLinesChange) {
        onSnapLinesChange([]);
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleSize = 8;
  const handleStyle = (cursor) => ({
    position: "absolute",
    width: handleSize,
    height: handleSize,
    background: isSelected ? "#1677ff" : "#888",
    borderRadius: "50%",
    cursor,
    zIndex: 10,
  });

  const resolveFont = () => {
    let resolvedFont = "Arial, sans-serif";

    if (region.fontFamily) {
      if (loadedWebFonts[region.fontFamily]) {
        return loadedWebFonts[region.fontFamily];
      }
      const fontObj = availableFonts.find(
        (f) =>
          f.filename === region.fontFamily ||
          f.name === region.fontFamily ||
          f.font_family === region.fontFamily,
      );
      if (fontObj) {
        if (loadedWebFonts[fontObj.filename])
          return loadedWebFonts[fontObj.filename];
        if (loadedWebFonts[fontObj.name]) return loadedWebFonts[fontObj.name];
      }
    }

    if (resolvedFont === "Arial, sans-serif") {
      const placeholderMatches =
        (region.content || "").match(/\{([A-Z0-9_]+)\}/gi) || [];
      if (placeholderMatches.length > 0) {
        const phName = placeholderMatches[0].replace(/[{}]/g, "").toUpperCase();
        const mappedFontFilename = fontMap[phName];
        if (mappedFontFilename && loadedWebFonts[mappedFontFilename]) {
          return loadedWebFonts[mappedFontFilename];
        }
      }
    }

    return resolvedFont;
  };

  const resolvedFont = resolveFont();

  return (
    <div
      ref={regionRef}
      onClick={handleRegionClick}
      onMouseDown={(e) => handleMouseDown(e, "move")}
      style={{
        position: "absolute",
        left: screenBox.left,
        top: screenBox.top,
        width: screenBox.width,
        height: screenBox.height,
        border: isSelected ? "2px solid #1677ff" : "1px dashed #1677ff",
        background: isEditing
          ? "rgba(255,255,255,0.95)"
          : isSelected
            ? "rgba(22, 119, 255, 0.05)"
            : "transparent",
        cursor: drawMode ? "crosshair" : isEditing ? "text" : "move",
        zIndex: isSelected ? 10 : 5,
        boxSizing: "border-box",
      }}
    >
      {isEditing ? (
        <textarea
          ref={editRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={finishEdit}
          onKeyDown={handleEditKeyDown}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            width: "100%",
            height: "100%",
            fontSize: (region.fontSize || 11) * scale,
            fontFamily: resolvedFont,
            color: region.color || "#333",
            lineHeight: region.lineHeight || 1.3,
            textAlign: region.align || "left",
            backgroundColor: "transparent",
            border: "none",
            outline: "none",
            resize: "none",
            padding: 2,
            margin: 0,
            overflow: "hidden",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            boxSizing: "border-box",
          }}
        />
      ) : region.content ? (
        <div
          style={{
            fontSize: (region.fontSize || 11) * scale,
            fontFamily: resolvedFont,
            color: region.color || "#333",
            lineHeight: region.lineHeight || 1.3,
            padding: 2,
            overflow: "hidden",
            height: "100%",
            pointerEvents: "none",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            textAlign: region.align || "left",
            backgroundColor: region.backgroundColor || "transparent",
          }}
        >
          {livePreview && previewData ? (
            <span style={{ color: region.color || "#333" }}>
              {replacePlaceholders(region.content, previewData)}
            </span>
          ) : (
            region.content.split(/(\{[A-Z0-9_]+\})/gi).map((part, i) =>
              part.match(/^\{[A-Z0-9_]+\}$/i) ? (
                <span key={i} style={{ color: "#1677ff", fontWeight: 600 }}>
                  {part}
                </span>
              ) : (
                part
              ),
            )
          )}
        </div>
      ) : null}

      {isSelected && !drawMode && !isEditing && (
        <>
          <div
            style={{
              ...handleStyle("nwse-resize"),
              top: -handleSize / 2,
              left: -handleSize / 2,
            }}
            onMouseDown={(e) => handleMouseDown(e, "nw")}
          />
          <div
            style={{
              ...handleStyle("nesw-resize"),
              top: -handleSize / 2,
              right: -handleSize / 2,
            }}
            onMouseDown={(e) => handleMouseDown(e, "ne")}
          />
          <div
            style={{
              ...handleStyle("nesw-resize"),
              bottom: -handleSize / 2,
              left: -handleSize / 2,
            }}
            onMouseDown={(e) => handleMouseDown(e, "sw")}
          />
          <div
            style={{
              ...handleStyle("nwse-resize"),
              bottom: -handleSize / 2,
              right: -handleSize / 2,
            }}
            onMouseDown={(e) => handleMouseDown(e, "se")}
          />
        </>
      )}
    </div>
  );
}

/**
 * Single Page Component - Renders one PDF page with its regions
 */
function PageView({
  pageNum,
  imageUrl,
  regions,
  scale,
  selectedRegionId,
  onSelectRegion,
  onUpdateRegion,
  drawMode,
  onDrawStart,
  onDrawMove,
  onDrawEnd,
  tempRect,
  isDrawingOnPage,
  pdfWidth,
  pdfHeight,
  livePreview = false,
  previewData = null,
  fontMap = {},
  availableFonts = [],
  loadedWebFonts = {},
  textGuides = [],
  showTextGuides = false,
  editingRegionId = null,
  onStartEdit,
  onTextGuideClick,
}) {
  const pageRef = useRef(null);
  const [activeSnapLines, setActiveSnapLines] = useState([]);

  const handleMouseDown = (e) => {
    if (!drawMode) return;
    const rect = pageRef.current.getBoundingClientRect();

    // Click position relative to container (which contains the image)
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Convert screen pixels to PDF coordinates (72 DPI)
    // The container/image is displayed at: pdfSize * scale pixels
    // So: PDF coord = screen coord / scale
    const x = clickX / scale;
    const y = clickY / scale;

    onDrawStart(pageNum, x, y);
  };

  const handleMouseMove = (e) => {
    if (!drawMode || !isDrawingOnPage) return;
    const rect = pageRef.current.getBoundingClientRect();

    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    const x = clickX / scale;
    const y = clickY / scale;

    onDrawMove(x, y);
  };

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Page label */}
      <div
        style={{
          fontSize: 11,
          color: "#666",
          marginBottom: 4,
          fontWeight: 500,
        }}
      >
        Page {pageNum + 1}
      </div>

      {/* Page container - size must match image exactly */}
      <div
        ref={pageRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={onDrawEnd}
        onMouseLeave={onDrawEnd}
        onClick={(e) => {
          if (
            !drawMode &&
            (e.target === pageRef.current || e.target.tagName === "IMG")
          ) {
            onSelectRegion(null);
          }
        }}
        style={{
          position: "relative",
          display: "inline-block",
          cursor: drawMode ? "crosshair" : "default",
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          borderRadius: 2,
          overflow: "hidden",
          padding: 0,
          margin: 0,
          border: "none",
          width: `${pdfWidth * scale}px`,
          height: `${pdfHeight * scale}px`,
        }}
      >
        <img
          src={imageUrl}
          alt={`Page ${pageNum + 1}`}
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            margin: 0,
            padding: 0,
            position: "absolute",
            top: 0,
            left: 0,
          }}
          draggable={false}
        />

        {/* Text alignment guides - clickable to create editable regions */}
        {showTextGuides &&
          textGuides
            .filter((t) => t.page === pageNum)
            .map((textBlock, idx) => (
              <Tooltip
                key={`guide-${idx}`}
                title={
                  <div style={{ fontSize: 11, lineHeight: 1.6 }}>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>
                      {textBlock.font || "Unknown Font"}
                    </div>
                    <div>Size: {textBlock.size}pt</div>
                    {textBlock.color && <div>Color: {textBlock.color}</div>}
                    <div
                      style={{
                        marginTop: 4,
                        color: "rgba(255,255,255,0.7)",
                        fontSize: 10,
                        borderTop: "1px solid rgba(255,255,255,0.2)",
                        paddingTop: 4,
                      }}
                    >
                      Click to edit
                    </div>
                  </div>
                }
                placement="top"
                mouseEnterDelay={0.3}
              >
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!drawMode && onTextGuideClick)
                      onTextGuideClick(textBlock);
                  }}
                  style={{
                    position: "absolute",
                    left: textBlock.bbox.x0 * scale,
                    top: textBlock.bbox.y0 * scale,
                    width: textBlock.bbox.width * scale,
                    height: textBlock.bbox.height * scale,
                    border: "1px dashed rgba(255, 152, 0, 0.6)",
                    background: "rgba(255, 152, 0, 0.08)",
                    pointerEvents: "auto",
                    cursor: "pointer",
                    zIndex: 2,
                  }}
                />
              </Tooltip>
            ))}

        {/* Regions for this page */}
        {regions.map((region) => (
          <Region
            key={region.id}
            region={region}
            scale={scale}
            isSelected={selectedRegionId === region.id}
            onSelect={onSelectRegion}
            onUpdate={onUpdateRegion}
            drawMode={drawMode}
            livePreview={livePreview}
            previewData={previewData}
            fontMap={fontMap}
            availableFonts={availableFonts}
            loadedWebFonts={loadedWebFonts}
            textGuides={textGuides}
            pageNum={pageNum}
            onSnapLinesChange={setActiveSnapLines}
            enableSnapping={showTextGuides}
            isEditing={editingRegionId === region.id}
            onStartEdit={onStartEdit}
          />
        ))}

        {/* Active snap/alignment lines - clean, minimal lines between elements */}
        {activeSnapLines.map((line, idx) =>
          line.type === "horizontal" ? (
            <div
              key={`snap-${idx}`}
              style={{
                position: "absolute",
                left: (line.x1 || 0) * scale,
                top: line.y * scale - 1,
                width: ((line.x2 || pdfWidth) - (line.x1 || 0)) * scale,
                height: 2,
                background: line.color || "#faad14",
                pointerEvents: "none",
                zIndex: 1000,
              }}
            />
          ) : (
            <div
              key={`snap-${idx}`}
              style={{
                position: "absolute",
                left: line.x * scale - 1,
                top: (line.y1 || 0) * scale,
                width: 2,
                height: ((line.y2 || pdfHeight) - (line.y1 || 0)) * scale,
                background: line.color || "#52c41a",
                pointerEvents: "none",
                zIndex: 1000,
              }}
            />
          ),
        )}

        {/* Temporary drawing rectangle */}
        {isDrawingOnPage && tempRect && (
          <div
            style={{
              position: "absolute",
              // tempRect is in PDF space (72 DPI), convert to screen using scale
              left: tempRect.x * scale,
              top: tempRect.y * scale,
              width: tempRect.width * scale,
              height: tempRect.height * scale,
              border: "2px dashed #1677ff",
              background: "rgba(22, 119, 255, 0.1)",
              pointerEvents: "none",
            }}
          />
        )}
      </div>
    </div>
  );
}

/**
 * PlaceholderVisualizer - Professional PDF editor with multi-page support
 */
export default function PlaceholderVisualizer({
  templateId,
  onRegionsChange,
  onError: onErrorCallback,
  placeholderSettings = {},
  fontMap = {},
  availablePlaceholders = [],
  availableFonts = [],
  dataFileId = null,
  initialPreviewData = null,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageImages, setPageImages] = useState([]); // Array of {pageNum, url}
  const [pdfDimensions, setPdfDimensions] = useState({
    width: 612,
    height: 792,
  });
  const [totalPages, setTotalPages] = useState(1);
  const [zoom, setZoom] = useState(50); // Default 50%

  // Regions state
  const [regions, setRegions] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState(null);
  const [editingRegionId, setEditingRegionId] = useState(null);

  // Drawing state
  const [drawMode, setDrawMode] = useState(false);
  const [drawingPage, setDrawingPage] = useState(null);
  const [drawStart, setDrawStart] = useState(null);
  const [tempRect, setTempRect] = useState(null);

  // Live preview state
  const [livePreview, setLivePreview] = useState(false);
  const [previewData, setPreviewData] = useState(initialPreviewData);
  const [previewRowIndex, setPreviewRowIndex] = useState(0);
  const [previewRows, setPreviewRows] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Text alignment guides - shows bounding boxes of existing PDF text
  const [textGuides, setTextGuides] = useState([]);
  const [showTextGuides, setShowTextGuides] = useState(false);
  const [loadingTextGuides, setLoadingTextGuides] = useState(false);

  // Loaded web fonts state - maps font filename to CSS font-family name
  const [loadedWebFonts, setLoadedWebFonts] = useState({});
  const fontStyleRef = useRef(null);
  const vizCancelledRef = useRef(false);
  const vizRevokedRef = useRef([]);

  // Scale calculation - converts PDF coordinates (72 DPI) to screen pixels
  // Image is rendered at 200 DPI and displayed with zoom
  // scale = (zoom/100) * (200/72)
  // This is the EXACT conversion factor - no approximations
  const scale = useMemo(() => {
    return (zoom / 100) * (200 / 72);
  }, [zoom]);

  // Load existing regions from placeholderSettings - reload when templateId or settings change
  useEffect(() => {
    if (placeholderSettings._customRegions?.length > 0) {
      setRegions(
        placeholderSettings._customRegions.map((r) => ({
          ...r,
          id: r.id || `region-${Date.now()}-${Math.random()}`,
          page: r.page || 0,
          content: r.content || "",
          fontSize: r.fontSize || 11,
          fontFamily: r.fontFamily || "",
          color: r.color || "#000000",
          align: r.align || "left",
          lineHeight: r.lineHeight || 1.3,
          backgroundColor: r.backgroundColor || "transparent",
        })),
      );
    } else {
      // Reset regions if no saved regions
      setRegions([]);
    }
  }, [templateId, placeholderSettings._customRegions]);

  // Load web fonts whenever fonts are available
  useEffect(() => {
    if (!availableFonts || availableFonts.length === 0) return;

    console.log(
      `Loading ${availableFonts.length} fonts for live preview...`,
      availableFonts,
    );

    const loadFonts = async () => {
      // Create or get the style element for font-face rules
      if (!fontStyleRef.current) {
        fontStyleRef.current = document.createElement("style");
        fontStyleRef.current.id = "pdf-parser-fonts";
        document.head.appendChild(fontStyleRef.current);
      }

      const fontFaceRules = [];
      const newLoadedFonts = {};

      for (const font of availableFonts) {
        if (!font.id) {
          console.warn("Skipping font without ID:", font);
          continue;
        }

        try {
          console.log(
            `Fetching font: ${font.name} (id=${font.id}, filename=${font.filename})`,
          );

          // Create a unique CSS font-family name
          const cssFontFamily = `PDFFont_${font.id}_${font.name?.replace(/\s+/g, "_") || "font"}`;

          // Fetch the font file as blob
          const blob = await getFontFile(font.id);
          console.log(
            `Got blob for ${font.name}: size=${blob.size}, type=${blob.type}`,
          );

          if (blob.size === 0) {
            console.error(`Font ${font.name} returned empty blob!`);
            continue;
          }

          const blobUrl = URL.createObjectURL(blob);

          // Determine font format
          const ext = (
            font.font_type ||
            font.filename?.split(".").pop() ||
            "ttf"
          ).toLowerCase();
          const formatMap = {
            ttf: "truetype",
            otf: "opentype",
            woff: "woff",
            woff2: "woff2",
          };
          const format = formatMap[ext] || "truetype";

          // Create @font-face rule
          const fontFaceRule = `
                        @font-face {
                            font-family: '${cssFontFamily}';
                            src: url('${blobUrl}') format('${format}');
                            font-weight: normal;
                            font-style: normal;
                            font-display: swap;
                        }
                    `;
          fontFaceRules.push(fontFaceRule);

          // Map ALL possible lookups to the CSS font-family
          newLoadedFonts[font.filename] = cssFontFamily;
          newLoadedFonts[font.name] = cssFontFamily;
          newLoadedFonts[font.id] = cssFontFamily;
          newLoadedFonts[`${font.id}`] = cssFontFamily; // String ID too
          if (font.font_family) {
            newLoadedFonts[font.font_family] = cssFontFamily;
          }

          console.log(`✅ Loaded font: ${font.name} -> ${cssFontFamily}`);
          console.log(
            `   Mappings: filename="${font.filename}", name="${font.name}", id=${font.id}`,
          );
        } catch (err) {
          console.error(`❌ Failed to load font ${font.name}:`, err);
        }
      }

      // Inject all font-face rules
      if (fontStyleRef.current && fontFaceRules.length > 0) {
        fontStyleRef.current.textContent = fontFaceRules.join("\n");
        console.log(`Injected ${fontFaceRules.length} @font-face rules`);
      }

      console.log("loadedWebFonts map:", newLoadedFonts);
      setLoadedWebFonts(newLoadedFonts);

      if (Object.keys(newLoadedFonts).length > 0) {
        const uniqueFonts = availableFonts.filter(
          (f) => newLoadedFonts[f.filename],
        ).length;
        message.success(`Loaded ${uniqueFonts} fonts`);
      }
    };

    loadFonts();

    return () => {
      if (fontStyleRef.current) {
        fontStyleRef.current.remove();
        fontStyleRef.current = null;
      }
    };
  }, [availableFonts]);

  // Fetch preview data when live preview is enabled
  useEffect(() => {
    if (!livePreview || !dataFileId) {
      return;
    }

    const fetchPreviewData = async () => {
      setLoadingPreview(true);
      try {
        const result = await getDataFilePreview(dataFileId, 10);
        const preview = result.preview || result.preview_data || [];

        if (preview && preview.length > 0) {
          setPreviewRows(preview);
          setPreviewData(preview[0]);
          setPreviewRowIndex(0);
          message.success(`Loaded preview with ${preview.length} sample rows`);
        } else {
          message.warning("No preview data available for this file");
          setLivePreview(false);
        }
      } catch (err) {
        console.error("Error fetching preview data:", err);
        message.error(
          "Failed to load preview data: " + (err.message || "Unknown error"),
        );
        setLivePreview(false);
      } finally {
        setLoadingPreview(false);
      }
    };

    fetchPreviewData();
  }, [livePreview, dataFileId]);

  // Update preview data when row index changes
  useEffect(() => {
    if (previewRows.length > 0 && previewRowIndex < previewRows.length) {
      setPreviewData(previewRows[previewRowIndex]);
    }
  }, [previewRowIndex, previewRows]);

  // Fetch text guides when enabled
  useEffect(() => {
    if (!showTextGuides || !templateId) {
      return;
    }

    // Only fetch if we don't have guides already
    if (textGuides.length > 0) {
      return;
    }

    const fetchTextGuides = async () => {
      setLoadingTextGuides(true);
      try {
        const result = await getTemplateTextPositions(templateId, null, false);
        const blocks = result?.data?.text_blocks || result?.text_blocks;
        if (blocks && blocks.length > 0) {
          setTextGuides(blocks);
          message.success(`Loaded ${blocks.length} text alignment guides`);
        } else if (result && !result.success) {
          message.error(result.message || "Failed to load text guides");
          setShowTextGuides(false);
        } else {
          message.info("No text guides found in this template");
        }
      } catch (err) {
        console.error("Error fetching text guides:", err);
        message.error("Failed to load text alignment guides");
        setShowTextGuides(false);
      } finally {
        setLoadingTextGuides(false);
      }
    };

    fetchTextGuides();
  }, [showTextGuides, templateId]);

  // Clear text guides when template changes
  useEffect(() => {
    setTextGuides([]);
    setShowTextGuides(false);
  }, [templateId]);

  // Navigate between preview rows
  const prevRow = () => {
    if (previewRowIndex > 0) {
      setPreviewRowIndex(previewRowIndex - 1);
    }
  };

  const nextRow = () => {
    if (previewRowIndex < previewRows.length - 1) {
      setPreviewRowIndex(previewRowIndex + 1);
    }
  };

  // Fetch preview page images automatically; cap at 5 pages. Cancel previous fetch when templateId changes.
  const stableTemplateId = templateId != null ? Number(templateId) : null;
  useEffect(() => {
    if (stableTemplateId == null) return;

    vizCancelledRef.current = false;
    vizRevokedRef.current = [];

    const fetchPages = async () => {
      setLoading(true);
      setError(null);

      try {
        const dataResult = await getTemplateVisualizationData(stableTemplateId);
        if (vizCancelledRef.current) return;
        const totalFromApi =
          dataResult.data?.data?.total_pages ||
          dataResult.data?.total_pages ||
          1;
        const width =
          dataResult.data?.data?.page_width ||
          dataResult.data?.page_width ||
          612;
        const height =
          dataResult.data?.data?.page_height ||
          dataResult.data?.page_height ||
          792;
        const MAX_PREVIEW_PAGES = 5;
        const pages = Math.min(Math.max(1, totalFromApi), MAX_PREVIEW_PAGES);

        setTotalPages(totalFromApi);
        setPdfDimensions({ width, height });

        const images = [];
        for (let i = 0; i < pages; i++) {
          if (vizCancelledRef.current) return;
          const blob = await getTemplateVisualizationImage(stableTemplateId, i);
          if (vizCancelledRef.current) return;
          const url = URL.createObjectURL(blob);
          vizRevokedRef.current.push(url);
          images.push({ pageNum: i, url });
        }

        if (vizCancelledRef.current) return;
        pageImages.forEach((img) => URL.revokeObjectURL(img.url));
        setPageImages(images);
        vizRevokedRef.current = []; // committed; don't revoke in cleanup
      } catch (err) {
        if (vizCancelledRef.current) return;
        console.error("Error loading pages:", err);
        const rawMsg =
          err.response?.data?.error ||
          err.message ||
          "Failed to load template visualization";
        const errorMsg =
          typeof rawMsg === "string" &&
          (rawMsg.includes("500") || rawMsg.includes("status code"))
            ? "Preview unavailable. You can still generate PDFs using Test (1 PDF) or Generate Batch above."
            : rawMsg;
        setError(errorMsg);
        onErrorCallback?.(errorMsg);
      } finally {
        if (!vizCancelledRef.current) setLoading(false);
      }
    };

    fetchPages();

    return () => {
      vizCancelledRef.current = true;
      vizRevokedRef.current.forEach((u) => URL.revokeObjectURL(u));
      vizRevokedRef.current = [];
    };
  }, [stableTemplateId]);

  // Drawing handlers
  const handleDrawStart = (pageNum, x, y) => {
    setDrawingPage(pageNum);
    setDrawStart({ x, y });
    setTempRect({ x, y, width: 0, height: 0 });
  };

  const handleDrawMove = (x, y) => {
    if (drawStart === null) return;
    setTempRect({
      x: Math.min(drawStart.x, x),
      y: Math.min(drawStart.y, y),
      width: Math.abs(x - drawStart.x),
      height: Math.abs(y - drawStart.y),
    });
  };

  const handleDrawEnd = () => {
    if (tempRect && tempRect.width >= 30 && tempRect.height >= 15) {
      const newRegion = {
        id: `region-${Date.now()}`,
        page: drawingPage,
        x: Math.round(tempRect.x),
        y: Math.round(tempRect.y),
        width: Math.round(tempRect.width),
        height: Math.round(tempRect.height),
        content: "",
        fontSize: 11,
        fontFamily: "",
        color: "#000000",
        align: "left",
        lineHeight: 1.3,
        backgroundColor: "transparent",
      };
      console.log(`[CREATE REGION] Region ${regions.length + 1}:`, {
        x: newRegion.x,
        y: newRegion.y,
        width: newRegion.width,
        height: newRegion.height,
        tempRect: tempRect,
        scale: scale,
      });
      setRegions((prev) => [...prev, newRegion]);
      setSelectedRegionId(newRegion.id);
      message.success(`Region created on page ${drawingPage + 1}`);
    }
    setDrawingPage(null);
    setDrawStart(null);
    setTempRect(null);
  };

  const sanitizePdfText = (text) => {
    if (!text) return text;
    return text
      .replace(/[\u2018\u2019\u201A\u201B\u0091\u0092]/g, "'")
      .replace(/[\u201C\u201D\u201E\u201F\u0093\u0094]/g, '"')
      .replace(/[\u2013\u2014\u0096\u0097]/g, "-")
      .replace(/[\u2026]/g, "...")
      .replace(/\uFB01/g, "fi")
      .replace(/\uFB02/g, "fl")
      .replace(/[\uFFFD]/g, "");
  };

  const createRegionFromTextGuide = (textBlock) => {
    // Find all spans in the same block to merge into one paragraph region
    const blockIndex = textBlock.block_index;
    const blockPage = textBlock.page;
    let siblingsInBlock = [];

    if (blockIndex !== undefined && blockIndex !== null) {
      siblingsInBlock = textGuides.filter(
        (t) =>
          t.page === blockPage &&
          t.block_index === blockIndex &&
          !t.is_placeholder,
      );
    }

    // If we found sibling spans, use the block bbox; otherwise fall back to single span
    const useBlock = siblingsInBlock.length > 1 && textBlock.block_bbox;

    let mergedX0, mergedY0, mergedX1, mergedY1, mergedText, fontSize;
    if (useBlock) {
      mergedX0 = textBlock.block_bbox.x0;
      mergedY0 = textBlock.block_bbox.y0;
      mergedX1 = textBlock.block_bbox.x1;
      mergedY1 = textBlock.block_bbox.y1;
      // Sort siblings by vertical then horizontal position and join text
      const sorted = [...siblingsInBlock].sort((a, b) =>
        a.bbox.y0 !== b.bbox.y0 ? a.bbox.y0 - b.bbox.y0 : a.bbox.x0 - b.bbox.x0,
      );
      mergedText = sorted
        .map((s) => sanitizePdfText(s.text))
        .filter(Boolean)
        .join(" ");
      fontSize = textBlock.size || sorted[0]?.size || 11;
    } else {
      mergedX0 = textBlock.bbox.x0;
      mergedY0 = textBlock.bbox.y0;
      mergedX1 = textBlock.bbox.x0 + textBlock.bbox.width;
      mergedY1 = textBlock.bbox.y0 + textBlock.bbox.height;
      mergedText = sanitizePdfText(textBlock.text) || "";
      fontSize = textBlock.size || 11;
    }

    // Check if a region already exists at this position
    const existingRegion = regions.find(
      (r) =>
        r.page === blockPage &&
        Math.abs(r.x - mergedX0) < 5 &&
        Math.abs(r.y - mergedY0) < 5,
    );
    if (existingRegion) {
      setSelectedRegionId(existingRegion.id);
      setEditingRegionId(existingRegion.id);
      return;
    }

    const bboxH = mergedY1 - mergedY0;
    const regionH = Math.max(bboxH, fontSize * 1.4);
    const yOffset = (regionH - bboxH) / 2;

    const newRegion = {
      id: `region-${Date.now()}`,
      page: blockPage,
      x: Math.round(mergedX0 - 1),
      y: Math.round(mergedY0 - yOffset),
      width: Math.round(mergedX1 - mergedX0 + 4),
      height: Math.round(regionH),
      content: mergedText,
      fontSize: fontSize,
      fontFamily: "",
      color: "#000000",
      align: "left",
      lineHeight: 1.3,
      backgroundColor: "white",
      _sourceFont: textBlock.font || null,
      _sourceFontSize: textBlock.size || null,
    };
    setRegions((prev) => [...prev, newRegion]);
    setSelectedRegionId(newRegion.id);
    setEditingRegionId(newRegion.id);
  };

  // Region management
  const updateRegion = (id, updates) => {
    setRegions((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...updates } : r)),
    );
  };

  const deleteRegion = (id) => {
    setRegions((prev) => prev.filter((r) => r.id !== id));
    if (selectedRegionId === id) setSelectedRegionId(null);
  };

  const saveRegions = () => {
    const regionsToSave = regions
      .filter((r) => r.content?.trim())
      .map((r, index) => {
        const saved = {
          id: r.id,
          page: r.page,
          x: Math.round(r.x),
          y: Math.round(r.y),
          width: Math.round(r.width),
          height: Math.round(r.height),
          content: r.content,
          fontSize: r.fontSize,
          fontFamily: r.fontFamily,
          color: r.color,
          align: r.align,
          lineHeight: r.lineHeight,
          backgroundColor: r.backgroundColor,
        };
        console.log(`[SAVE REGION] Region ${index + 1}:`, {
          id: saved.id,
          x: saved.x,
          y: saved.y,
          width: saved.width,
          height: saved.height,
          original: { x: r.x, y: r.y, width: r.width, height: r.height },
        });
        return saved;
      });

    if (regionsToSave.length === 0) {
      message.warning("Add content to at least one region before saving");
      return;
    }

    onRegionsChange?.(regionsToSave);
    message.success(`Saved ${regionsToSave.length} region(s)`);
  };

  const selectedRegion = regions.find((r) => r.id === selectedRegionId);
  const allPlaceholders = [...new Set([...availablePlaceholders])];

  if (loading && pageImages.length === 0) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    const isFriendly = error.includes("You can still generate");
    return (
      <div
        style={{
          padding: 20,
          textAlign: "center",
          color: isFriendly ? "#666" : "#ff4d4f",
          background: isFriendly ? "#fafafa" : "transparent",
          borderRadius: 8,
          border: isFriendly ? "1px solid #e8e8e8" : "none",
        }}
      >
        {isFriendly ? <span>{error}</span> : <span>Error: {error}</span>}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", gap: 16, height: "70vh", minHeight: 500 }}>
      {/* Left: PDF Pages */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        {/* Toolbar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 0",
            borderBottom: "1px solid #e8e8e8",
            marginBottom: 12,
            flexWrap: "wrap",
            flexShrink: 0,
          }}
        >
          <Button
            type={drawMode ? "primary" : "default"}
            icon={<PlusOutlined />}
            onClick={() => setDrawMode(!drawMode)}
            size="small"
          >
            {drawMode ? "Drawing..." : "Draw Region"}
          </Button>

          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={saveRegions}
            disabled={regions.filter((r) => r.content?.trim()).length === 0}
            size="small"
          >
            Save ({regions.filter((r) => r.content?.trim()).length})
          </Button>

          {/* Live Preview Toggle */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginLeft: 8,
              padding: "4px 8px",
              background: livePreview ? "#e6f4ff" : "#f5f5f5",
              borderRadius: 6,
              border: livePreview ? "1px solid #91caff" : "1px solid #d9d9d9",
            }}
          >
            <Tooltip
              title={
                dataFileId
                  ? "Show actual data values in regions"
                  : "Upload data file to enable preview"
              }
            >
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <EyeOutlined
                  style={{ color: livePreview ? "#1677ff" : "#888" }}
                />
                <span
                  style={{
                    fontSize: 11,
                    color: livePreview ? "#1677ff" : "#666",
                  }}
                >
                  Live Preview
                </span>
                <Switch
                  size="small"
                  checked={livePreview}
                  onChange={(checked) => setLivePreview(checked)}
                  disabled={!dataFileId}
                  loading={loadingPreview}
                />
              </div>
            </Tooltip>

            {/* Text Alignment Guides Toggle */}
            <Tooltip title="Show bounding boxes of existing PDF text for alignment (hover to see text content)">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  marginLeft: 8,
                  paddingLeft: 8,
                  borderLeft: "1px solid #d9d9d9",
                }}
              >
                <AlignLeftOutlined
                  style={{ color: showTextGuides ? "#fa8c16" : "#888" }}
                />
                <span
                  style={{
                    fontSize: 11,
                    color: showTextGuides ? "#fa8c16" : "#666",
                  }}
                >
                  Text Guides
                </span>
                <Switch
                  size="small"
                  checked={showTextGuides}
                  onChange={(checked) => setShowTextGuides(checked)}
                  loading={loadingTextGuides}
                />
                {textGuides.length > 0 && showTextGuides && (
                  <span style={{ fontSize: 10, color: "#888" }}>
                    ({textGuides.length})
                  </span>
                )}
              </div>
            </Tooltip>

            {/* Row navigation */}
            {livePreview && previewRows.length > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  marginLeft: 4,
                  paddingLeft: 8,
                  borderLeft: "1px solid #d9d9d9",
                }}
              >
                <Button
                  size="small"
                  icon={<LeftOutlined />}
                  onClick={prevRow}
                  disabled={previewRowIndex === 0}
                  style={{ padding: "0 4px", height: 20, width: 20 }}
                />
                <span
                  style={{
                    fontSize: 10,
                    minWidth: 45,
                    textAlign: "center",
                    color: "#666",
                  }}
                >
                  Row {previewRowIndex + 1}/{previewRows.length}
                </span>
                <Button
                  size="small"
                  icon={<RightOutlined />}
                  onClick={nextRow}
                  disabled={previewRowIndex >= previewRows.length - 1}
                  style={{ padding: "0 4px", height: 20, width: 20 }}
                />
              </div>
            )}
          </div>

          {/* Zoom controls - fixed levels: 50%, 75%, 100% */}
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Button
              size="small"
              icon={<ZoomOutOutlined />}
              onClick={() =>
                setZoom((z) => (z === 100 ? 75 : z === 75 ? 50 : 50))
              }
              disabled={zoom === 50}
            />
            <span style={{ fontSize: 12, minWidth: 40, textAlign: "center" }}>
              {zoom}%
            </span>
            <Button
              size="small"
              icon={<ZoomInOutlined />}
              onClick={() =>
                setZoom((z) => (z === 50 ? 75 : z === 75 ? 100 : 100))
              }
              disabled={zoom === 100}
            />
          </div>
        </div>

        {/* Pages scroll container - scrolls independently */}
        <div
          style={{
            flex: 1,
            overflow: "auto",
            background: "#f0f0f0",
            padding: 16,
            borderRadius: 4,
            minHeight: 0,
          }}
        >
          {pageImages.map(({ pageNum, url }) => (
            <PageView
              key={pageNum}
              pageNum={pageNum}
              imageUrl={url}
              regions={regions.filter((r) => r.page === pageNum)}
              scale={scale}
              selectedRegionId={selectedRegionId}
              onSelectRegion={setSelectedRegionId}
              onUpdateRegion={updateRegion}
              drawMode={drawMode}
              onDrawStart={handleDrawStart}
              onDrawMove={handleDrawMove}
              onDrawEnd={handleDrawEnd}
              tempRect={drawingPage === pageNum ? tempRect : null}
              isDrawingOnPage={drawingPage === pageNum}
              pdfWidth={pdfDimensions.width}
              pdfHeight={pdfDimensions.height}
              livePreview={livePreview}
              previewData={previewData}
              fontMap={fontMap}
              availableFonts={availableFonts}
              loadedWebFonts={loadedWebFonts}
              textGuides={textGuides}
              showTextGuides={showTextGuides}
              editingRegionId={editingRegionId}
              onStartEdit={setEditingRegionId}
              onTextGuideClick={createRegionFromTextGuide}
            />
          ))}
        </div>
      </div>

      {/* Right: Region Editor - stays fixed while PDF scrolls */}
      <div
        style={{
          width: 280,
          flexShrink: 0,
          borderLeft: "1px solid #e8e8e8",
          paddingLeft: 16,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        <div
          style={{
            fontWeight: 600,
            fontSize: 13,
            marginBottom: 12,
            color: "#333",
          }}
        >
          Region Editor
        </div>

        {selectedRegion ? (
          <div style={{ flex: 1, overflow: "auto" }}>
            {/* Position info */}
            <div style={{ fontSize: 11, color: "#888", marginBottom: 12 }}>
              Page {selectedRegion.page + 1} •{" "}
              {Math.round(selectedRegion.width)}×
              {Math.round(selectedRegion.height)}@ (
              {Math.round(selectedRegion.x)}, {Math.round(selectedRegion.y)})
            </div>

            {/* Source font info - shown when region was created from text guide */}
            {selectedRegion._sourceFont && (
              <div
                style={{
                  marginBottom: 12,
                  padding: 8,
                  background: "#fff7e6",
                  border: "1px solid #ffd591",
                  borderRadius: 6,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    color: "#d46b08",
                    fontWeight: 600,
                    marginBottom: 2,
                  }}
                >
                  Original PDF Font
                </div>
                <div style={{ fontSize: 12, color: "#333", fontWeight: 600 }}>
                  {selectedRegion._sourceFont}
                </div>
                {selectedRegion._sourceFontSize && (
                  <div style={{ fontSize: 11, color: "#666" }}>
                    Size: {selectedRegion._sourceFontSize}pt
                  </div>
                )}
              </div>
            )}

            {/* Content */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>
                Content Template
              </div>
              <TextArea
                value={selectedRegion.content || ""}
                onChange={(e) =>
                  updateRegion(selectedRegion.id, { content: e.target.value })
                }
                placeholder="Type content with {PLACEHOLDERS}"
                rows={4}
                style={{ fontSize: 12 }}
              />
            </div>

            {/* Live Preview Display */}
            {livePreview && previewData && selectedRegion.content && (
              <div
                style={{
                  marginBottom: 12,
                  padding: 8,
                  background: "#f6ffed",
                  border: "1px solid #b7eb8f",
                  borderRadius: 6,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    color: "#52c41a",
                    fontWeight: 600,
                    marginBottom: 4,
                  }}
                >
                  📝 Preview (Row {previewRowIndex + 1})
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "#333",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: 80,
                    overflow: "auto",
                  }}
                >
                  {replacePlaceholders(selectedRegion.content, previewData)}
                </div>

                {/* Show available columns for debugging */}
                <div
                  style={{
                    fontSize: 9,
                    color: "#888",
                    marginTop: 6,
                    paddingTop: 6,
                    borderTop: "1px dashed #d9d9d9",
                  }}
                >
                  <strong>Available columns:</strong>{" "}
                  {Object.keys(previewData).slice(0, 8).join(", ")}
                  {Object.keys(previewData).length > 8 && "..."}
                </div>
              </div>
            )}

            {/* No data hint */}
            {!dataFileId &&
              selectedRegion.content &&
              selectedRegion.content.includes("{") && (
                <div
                  style={{
                    marginBottom: 12,
                    padding: 8,
                    background: "#fff7e6",
                    border: "1px solid #ffd591",
                    borderRadius: 6,
                    fontSize: 10,
                    color: "#d46b08",
                  }}
                >
                  💡 Upload a data file above to enable Live Preview and see
                  actual values
                </div>
              )}

            {/* Insert placeholder */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>
                Insert Placeholder
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {allPlaceholders.slice(0, 20).map((ph) => (
                  <Button
                    key={ph}
                    size="small"
                    type="text"
                    style={{
                      fontSize: 10,
                      padding: "2px 6px",
                      height: "auto",
                      background: "#f5f5f5",
                      border: "1px solid #e8e8e8",
                    }}
                    onClick={() => {
                      const newContent =
                        (selectedRegion.content || "") + `{${ph}}`;
                      updateRegion(selectedRegion.id, { content: newContent });
                    }}
                  >
                    {ph}
                  </Button>
                ))}
              </div>
            </div>

            {/* Font Size */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>
                Font Size
              </div>
              <InputNumber
                value={selectedRegion.fontSize}
                onChange={(v) =>
                  updateRegion(selectedRegion.id, { fontSize: v || 11 })
                }
                min={6}
                max={72}
                size="small"
                style={{ width: "100%" }}
              />
            </div>

            {/* Font Family */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>
                Font
              </div>
              <Select
                value={selectedRegion.fontFamily || ""}
                onChange={(v) =>
                  updateRegion(selectedRegion.id, { fontFamily: v })
                }
                size="small"
                style={{ width: "100%" }}
                placeholder="Default (Helvetica)"
                showSearch
                optionFilterProp="children"
              >
                <Select.Option value="">Default (Helvetica)</Select.Option>
                {(() => {
                  const seen = new Set();
                  return availableFonts
                    .filter((f) => {
                      const key = f.name || f.font_family || f.filename;
                      if (seen.has(key)) return false;
                      seen.add(key);
                      return true;
                    })
                    .map((f) => (
                      <Select.Option key={f.filename} value={f.filename}>
                        {f.name ||
                          f.font_family ||
                          f.filename.replace(/\.[^/.]+$/, "")}
                      </Select.Option>
                    ));
                })()}
              </Select>
              {availableFonts.length === 0 && (
                <div style={{ fontSize: 10, color: "#999", marginTop: 2 }}>
                  No custom fonts available. Upload fonts in Settings.
                </div>
              )}
            </div>

            {/* Text Color */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>
                Text Color
              </div>
              <input
                type="color"
                value={selectedRegion.color || "#000000"}
                onChange={(e) =>
                  updateRegion(selectedRegion.id, { color: e.target.value })
                }
                style={{
                  width: "100%",
                  height: 28,
                  cursor: "pointer",
                  border: "1px solid #d9d9d9",
                  borderRadius: 4,
                }}
              />
            </div>

            {/* Background Color */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>
                Background
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <Button
                  size="small"
                  type={
                    selectedRegion.backgroundColor === "transparent" ||
                    !selectedRegion.backgroundColor
                      ? "primary"
                      : "default"
                  }
                  onClick={() =>
                    updateRegion(selectedRegion.id, {
                      backgroundColor: "transparent",
                    })
                  }
                  style={{ fontSize: 11 }}
                >
                  None
                </Button>
                <Button
                  size="small"
                  type={
                    selectedRegion.backgroundColor === "white" ||
                    selectedRegion.backgroundColor === "#ffffff"
                      ? "primary"
                      : "default"
                  }
                  onClick={() =>
                    updateRegion(selectedRegion.id, {
                      backgroundColor: "white",
                    })
                  }
                  style={{ fontSize: 11 }}
                >
                  White
                </Button>
                <input
                  type="color"
                  value={
                    selectedRegion.backgroundColor &&
                    selectedRegion.backgroundColor !== "transparent" &&
                    selectedRegion.backgroundColor !== "white"
                      ? selectedRegion.backgroundColor
                      : "#ffffff"
                  }
                  onChange={(e) =>
                    updateRegion(selectedRegion.id, {
                      backgroundColor: e.target.value,
                    })
                  }
                  style={{
                    width: 28,
                    height: 28,
                    cursor: "pointer",
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                    padding: 0,
                  }}
                  title="Custom color"
                />
              </div>
            </div>

            {/* Alignment */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>
                Alignment
              </div>
              <Select
                value={selectedRegion.align || "left"}
                onChange={(v) => updateRegion(selectedRegion.id, { align: v })}
                size="small"
                style={{ width: "100%" }}
              >
                <Select.Option value="left">Left</Select.Option>
                <Select.Option value="center">Center</Select.Option>
                <Select.Option value="right">Right</Select.Option>
              </Select>
            </div>

            {/* Line Height */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>
                Line Height
              </div>
              <InputNumber
                value={selectedRegion.lineHeight}
                onChange={(v) =>
                  updateRegion(selectedRegion.id, { lineHeight: v || 1.3 })
                }
                min={1}
                max={3}
                step={0.1}
                size="small"
                style={{ width: "100%" }}
              />
            </div>

            {/* Delete */}
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => deleteRegion(selectedRegion.id)}
              size="small"
              block
            >
              Delete Region
            </Button>
          </div>
        ) : (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#999",
              fontSize: 12,
              textAlign: "center",
              padding: 20,
            }}
          >
            {regions.length === 0
              ? "Click 'Draw Region' then drag on the PDF to create a text region"
              : "Click a region to edit it"}
          </div>
        )}

        {/* Region list */}
        {regions.length > 0 && (
          <div
            style={{
              borderTop: "1px solid #e8e8e8",
              paddingTop: 12,
              marginTop: 12,
            }}
          >
            <div style={{ fontSize: 11, color: "#666", marginBottom: 8 }}>
              All Regions ({regions.length})
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                maxHeight: 150,
                overflow: "auto",
              }}
            >
              {regions.map((r) => (
                <div
                  key={r.id}
                  onClick={() => setSelectedRegionId(r.id)}
                  style={{
                    padding: "4px 8px",
                    background:
                      r.id === selectedRegionId ? "#e6f4ff" : "#fafafa",
                    border:
                      r.id === selectedRegionId
                        ? "1px solid #1677ff"
                        : "1px solid #e8e8e8",
                    borderRadius: 4,
                    cursor: "pointer",
                    fontSize: 11,
                  }}
                >
                  <span style={{ color: "#888" }}>P{r.page + 1}</span>{" "}
                  {r.content ? (
                    r.content.substring(0, 25) +
                    (r.content.length > 25 ? "..." : "")
                  ) : (
                    <span style={{ color: "#ccc" }}>Empty</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
